import { useTranslation } from 'react-i18next'
import { Stack, Box, Button } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import {
  ControlledTextField,
  ControlledCheckboxField,
} from '@/components/inputs'
import { FormComponentProps, HoursReport, PatchHoursReport } from '@/types'
import { hoursReportInfoSchema } from './validationSchema'

export type HoursReportFormData = Pick<HoursReport, 'research_visit_event_id'> &
  PatchHoursReport

export const HoursReportForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<HoursReportFormData>): JSX.Element => {
  const { t } = useTranslation()

  const methods = useForm<HoursReportFormData>({
    mode: 'onChange',
    defaultValues: {
      research_time_minutes: 0,
      research_time_comment: '',
      travel_time_minutes: 0,
      travel_time_comment: '',
      reporting_time_minutes: 0,
      reporting_time_comment: '',
      travel_distance_km: 0,
      travel_distance_comment: '',
      validation_message: '',
      reported_as_coupled: false,
      car_pooling: false,
    },
    values: defaultValues,
    resolver: yupResolver(hoursReportInfoSchema),
  })

  useApiErrorData<HoursReportFormData>(apiErrorData, methods)

  const handleSubmit = methods.handleSubmit((formData) => {
    onSubmit(formData)
  })

  const handleReset = () =>
    methods.reset({
      ...defaultValues,
    })

  return (
    <FormProvider {...methods}>
      <Stack spacing={5} mt={4} width="100%">
        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box width={200}>
            <ControlledTextField
              name="research_time_minutes"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.researchTimeMinutes'
              )}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
          <Box flexGrow={1}>
            <ControlledTextField
              name="research_time_comment"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.researchTimeComment'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box width={200}>
            <ControlledTextField
              name="travel_time_minutes"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.travelTimeMinutes'
              )}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
          <Box flexGrow={1}>
            <ControlledTextField
              name="travel_time_comment"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.travelTimeComment'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box width={200}>
            <ControlledTextField
              name="travel_distance_km"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.travelDistanceKm'
              )}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
          <Box flexGrow={1}>
            <ControlledTextField
              name="travel_distance_comment"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.travelDistanceComment'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box width={200}>
            <ControlledTextField
              name="reporting_time_minutes"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.reportingTimeMinutes'
              )}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
          <Box flexGrow={1}>
            <ControlledTextField
              name="reporting_time_comment"
              control={methods.control}
              placeholder={t(
                'hoursReporting:hours-report.fieldTitles.reportingTimeComment'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
              apiErrorData={apiErrorData}
            />
          </Box>
        </Stack>

        <ControlledTextField
          name="validation_message"
          control={methods.control}
          placeholder={t(
            'hoursReporting:hours-report.fieldTitles.validationComment'
          )}
          multiline={true}
          rows={2}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          errorMessagePrefix=""
          apiErrorData={apiErrorData}
        />

        <ControlledCheckboxField
          name="reported_as_coupled"
          control={methods.control}
          label={t('hoursReporting:hours-report.fieldTitles.reportedAsCoupled')}
        />
        <ControlledCheckboxField
          name="car_pooling"
          control={methods.control}
          label={t('hoursReporting:hours-report.fieldTitles.carPooling')}
        />

        {methods.formState.isDirty && (
          <Stack direction="row" spacing={5} pt={1}>
            <Button variant="textThin" color="secondary" onClick={handleReset}>
              {t('forms:buttons.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              loading={isLoading}
              disableElevation={true}
              disabled={!methods.formState.isDirty}
            >
              <span>{t('forms:buttons.saveCorrect')}</span>
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </FormProvider>
  )
}
